import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { LocationData, GridCell, CellType } from '../types';
import { computeFacilitySafety } from '../utils/facilitySafety';

interface SiteLayoutEditorProps {
  currentLocation: string;
  locations: Record<string, LocationData>;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  onClose: () => void;
  triggerNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
  onOpenFloor?: (floorId: string) => void;
  onOpenRoom?: (roomId: string, template: 'full' | 'half') => void;
}

type Tool =
  | 'move'
  | 'wall'
  | 'door'
  | 'walkway'
  | 'room'
  | 'label'
  | 'eraser'
  | 'tourniquetDoor'
  | 'extinguisher'
  | 'bin'
  | 'cabinet';

const TILE_W = 128;
const TILE_H = 96;
const SCALE = 1; // base world scale; zoom is managed dynamically
const SNAP = 1;  // snap unit in tiles (1×1)

const SiteLayoutEditor: React.FC<SiteLayoutEditorProps> = ({
  currentLocation,
  locations,
  setLocations,
  onClose,
  triggerNotification,
  onOpenFloor,
  onOpenRoom
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [selectedTool, setSelectedTool] = useState<Tool>('room');
  const scaleRef = useRef<number>(SCALE);
  const offsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const isPanningRef = useRef<boolean>(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const [hoverInfo, setHoverInfo] = useState<{ tile: { x: number; y: number }; snap: { x: number; y: number }; cellType?: string } | null>(null);
  // Placement state for room tool
  const placingRef = useRef<{ start: { x: number; y: number }; current: { x: number; y: number } } | null>(null);
  // Painting state for walls/walkways/doors/eraser
  const paintingRef = useRef<{ active: boolean; type: 'wall' | 'door' | 'walkway' | 'eraser' | 'tourniquetDoor' | 'extinguisher' | 'bin' | 'cabinet' } | null>(null);
  // Dragging state for moving rooms
  const draggingRoomRef = useRef<{ roomId: string; offset: { dx: number; dy: number } } | null>(null);
  const lastWarnRef = useRef<number>(0);
  const initializedRef = useRef<boolean>(false);

  // Tool ordering and labels for sidebar ('label' omitted — placement not implemented)
  const TOOL_ORDER: Tool[] = ['move','wall','door','walkway','room','eraser','tourniquetDoor','extinguisher','bin','cabinet'];
  const TOOL_LABELS: Record<Tool, string> = {
    move: 'Move',
    wall: 'Wall',
    door: 'Door',
    walkway: 'Walkway',
    room: 'Room',
    label: 'Label',
    eraser: 'Eraser',
    tourniquetDoor: 'Tourniquet Door',
    extinguisher: 'Extinguisher',
    bin: 'Bin',
    cabinet: 'Cabinet',
  };

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x: offX, y: offY } = offsetRef.current;
    const scale = scaleRef.current;

    // Clear background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#111827'; // gray-900
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // World transform
    ctx.save();
    ctx.translate(offX, offY);
    ctx.scale(scale, scale);

    // Outer border in world coords
    ctx.strokeStyle = '#6b7280'; // gray-500
    ctx.lineWidth = 1 / scale; // keep 1px in screen space
    ctx.strokeRect(0.5, 0.5, TILE_W - 1, TILE_H - 1);

    // Major grid lines every SNAP tiles
    ctx.strokeStyle = '#374151'; // gray-700
    ctx.lineWidth = 1 / scale;
    for (let x = 0; x <= TILE_W; x += SNAP) {
      ctx.beginPath();
      ctx.moveTo(x + 0.5, 0);
      ctx.lineTo(x + 0.5, TILE_H);
      ctx.stroke();
    }
    for (let y = 0; y <= TILE_H; y += SNAP) {
      ctx.beginPath();
      ctx.moveTo(0, y + 0.5);
      ctx.lineTo(TILE_W, y + 0.5);
      ctx.stroke();
    }

    // Hover snap highlight (tile/block)
    if (hoverInfo) {
      const { snap } = hoverInfo;
      ctx.fillStyle = 'rgba(139, 92, 246, 0.15)'; // purple-500 @ 15%
      ctx.strokeStyle = '#8b5cf6';
      ctx.lineWidth = 1 / scale;
      const onePx = 1 / scale;
      const halfPx = 0.5 / scale;
      const size = Math.max(1, SNAP);
      const block = size >= 2 ? size - 1 : 1; // ensure visible 1×1 when SNAP=1
      ctx.fillRect(snap.x + halfPx, snap.y + halfPx, block - (block === 1 ? onePx : 0), block - (block === 1 ? onePx : 0));
      ctx.strokeRect(snap.x + halfPx, snap.y + halfPx, block - (block === 1 ? onePx : 0), block - (block === 1 ? onePx : 0));
    }

    // Draw site cells (walls/walkways/doors)
    const siteCells = (locations[currentLocation]?.siteLayout?.cells as any) || null;
    if (siteCells) {
      const onePx = 1 / scale;
      const halfPx = 0.5 / scale;
      for (let y = 0; y < TILE_H; y++) {
        for (let x = 0; x < TILE_W; x++) {
          const c = siteCells[y][x];
          if (!c) continue;
          if (c.type === 'walkway') {
            ctx.fillStyle = 'rgba(148, 163, 184, 0.25)'; // slate-400 @ 25%
            ctx.fillRect(x + halfPx, y + halfPx, 1 - onePx, 1 - onePx);
          } else if (c.type === 'wall') {
            ctx.fillStyle = '#9ca3af'; // gray-400
            ctx.fillRect(x + halfPx, y + halfPx, 1 - onePx, 1 - onePx);
          } else if (c.type === 'door') {
            // Slim 1-tile door bar (horizontal) centered in the cell
            ctx.fillStyle = '#22c55e'; // green-500
            const doorH = Math.max(onePx, 0.18);
            ctx.fillRect(x + halfPx, y + 0.5 - doorH / 2, 1 - onePx, doorH);
          } else if (c.type === 'tourniquetDoor') {
            // Cyan posts to indicate turnstile door
            ctx.fillStyle = '#06b6d4'; // cyan-500
            const postW = Math.max(onePx, 0.12);
            const marginX = 0.18;
            const marginY = 0.14;
            ctx.fillRect(x + marginX, y + marginY, postW, 1 - 2 * marginY);
            ctx.fillRect(x + 1 - marginX - postW, y + marginY, postW, 1 - 2 * marginY);
            // Center bar
            ctx.strokeStyle = '#06b6d4';
            ctx.lineWidth = 1 / scale;
            ctx.beginPath();
            ctx.moveTo(x + 0.5, y + marginY);
            ctx.lineTo(x + 0.5, y + 1 - marginY);
            ctx.stroke();
          } else if (c.type === 'extinguisher') {
            // Fire extinguisher: red canister with white label and nozzle
            const bx = x + 0.28;
            const by = y + 0.18;
            const bw = 0.44;
            const bh = 0.64;
            // body
            ctx.fillStyle = '#ef4444'; // red-500
            ctx.fillRect(bx, by, bw, bh);
            // outline
            ctx.strokeStyle = '#111827'; // gray-900
            ctx.lineWidth = 1 / scale;
            ctx.strokeRect(bx, by, bw, bh);
            // white label
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(bx + 0.05, by + bh * 0.45, bw - 0.10, Math.max(onePx, 0.10));
            // nozzle/handle
            ctx.strokeStyle = '#e5e7eb'; // gray-200
            ctx.beginPath();
            ctx.moveTo(bx + bw - 0.02, by + 0.08);
            ctx.lineTo(bx + bw + 0.12, by + 0.02);
            ctx.stroke();
          } else if (c.type === 'bin') {
            // Bin: metallic trash can with dark lid and vertical ribs
            const pad = 0.14;
            const bx = x + pad;
            const by = y + 0.20;
            const bw = 1 - 2 * pad;
            const bh = 0.60;
            // body
            ctx.fillStyle = '#d1d5db'; // gray-300
            ctx.fillRect(bx, by, bw, bh);
            // outline
            ctx.strokeStyle = '#111827'; // gray-900
            ctx.lineWidth = 1 / scale;
            ctx.strokeRect(bx, by, bw, bh);
            // lid (slight overhang)
            const lidH = Math.max(onePx, 0.10);
            const lidX = bx - 0.05;
            const lidW = bw + 0.10;
            const lidY = by - lidH - Math.max(onePx, 0.02);
            ctx.fillStyle = '#374151'; // gray-700
            ctx.fillRect(lidX, lidY, lidW, lidH);
            // ribs
            ctx.strokeStyle = '#6b7280'; // gray-500
            ctx.beginPath();
            ctx.moveTo(bx + bw * 0.25, by + 0.06);
            ctx.lineTo(bx + bw * 0.25, by + bh - 0.06);
            ctx.moveTo(bx + bw * 0.50, by + 0.06);
            ctx.lineTo(bx + bw * 0.50, by + bh - 0.06);
            ctx.moveTo(bx + bw * 0.75, by + 0.06);
            ctx.lineTo(bx + bw * 0.75, by + bh - 0.06);
            ctx.stroke();
            // small side handles
            const handleW = Math.max(onePx, 0.06);
            const handleH = Math.max(onePx, 0.10);
            ctx.fillStyle = '#9ca3af'; // gray-400
            ctx.fillRect(bx - handleW, by + 0.18, handleW, handleH);
            ctx.fillRect(bx + bw, by + 0.18, handleW, handleH);
          } else if (c.type === 'cabinet') {
            // Cabinet: high-contrast body with center seam and handles
            const pad = 0.12;
            const bx = x + pad;
            const by = y + pad;
            const bw = 1 - 2 * pad;
            const bh = 1 - 2 * pad;
            ctx.fillStyle = '#64748b'; // slate-500
            ctx.fillRect(bx, by, bw, bh);
            // outline
            ctx.strokeStyle = '#0f172a'; // slate-900
            ctx.lineWidth = 1 / scale;
            ctx.strokeRect(bx, by, bw, bh);
            // center seam
            ctx.strokeStyle = '#e2e8f0'; // slate-100
            ctx.beginPath();
            ctx.moveTo(x + 0.5, by + 0.08);
            ctx.lineTo(x + 0.5, by + bh - 0.08);
            ctx.stroke();
            // handles
            ctx.fillStyle = '#e2e8f0';
            ctx.fillRect(x + 0.5 - 0.12, by + bh * 0.45, Math.max(onePx, 0.04), Math.max(onePx, 0.10));
            ctx.fillRect(x + 0.5 + 0.08, by + bh * 0.45, Math.max(onePx, 0.04), Math.max(onePx, 0.10));
          }
        }
      }
    }

    // Draw existing rooms from location state
    const roomsMap = (locations[currentLocation]?.rooms as Record<string, any>) || {};
    Object.values(roomsMap).forEach((room: any) => {
      if (!room?.bounds) return;
      const { x, y, width, height } = room.bounds;
      const hasFloor = !!room.floorId;
      ctx.fillStyle = (room.color ? `${room.color}33` : 'rgba(37, 99, 235, 0.2)');
      ctx.strokeStyle = room.color || '#2563eb';
      const onePx = 1 / scale;        // one device pixel in tile units
      const halfPx = 0.5 / scale;     // half pixel for crisp alignment
      ctx.lineWidth = 1 / scale;
      // Fill full footprint (18×8)
      ctx.fillRect(x, y, width, height);
      // Stroke aligned to pixel grid (crisp 1px), enclosing the footprint without inset
      ctx.strokeRect(x + halfPx, y + halfPx, width - onePx, height - onePx);
      if (hasFloor) {
        ctx.fillStyle = '#10b981'; // emerald-500
        ctx.fillRect(x + width - 2.5, y + 1, 1.5, 1.5);
      }
      // Label
      ctx.fillStyle = '#e5e7eb';
      ctx.font = `${12 / scale}px sans-serif`;
      const label = room.name || room.id;
      if (label) ctx.fillText(label, x + 4, y + 12 / scale + 0.5);
    });

    // Preview rectangle while placing a room
    if (placingRef.current && selectedTool === 'room') {
      const { current } = placingRef.current;
      const px = Math.max(0, Math.min(TILE_W - 18, current.x));
      const py = Math.max(0, Math.min(TILE_H - 8, current.y));
      ctx.save();
      ctx.setLineDash([6 / scale, 6 / scale]);
      ctx.strokeStyle = '#f59e0b'; // amber-500
      const onePx = 1 / scale;
      const half = 0.5 / scale;
      ctx.lineWidth = 1 / scale;
      // Preview matches final draw: crisp 1px border enclosing an exact 18×8
      ctx.strokeRect(px + half, py + half, 18 - onePx, 8 - onePx);
      ctx.restore();
    }

    ctx.restore();

    // UI text overlay
    ctx.fillStyle = '#9ca3af';
    ctx.font = '12px sans-serif';
    ctx.fillText(`Tool: ${selectedTool} | Zoom: ${scale.toFixed(2)}x`, 12, 20);
  }, [hoverInfo, selectedTool, locations, currentLocation]);

  const paintAt = useCallback((x: number, y: number, tool: 'wall' | 'door' | 'walkway' | 'eraser' | 'tourniquetDoor' | 'extinguisher' | 'bin' | 'cabinet') => {
    setLocations(prev => {
      const copy = { ...prev } as Record<string, LocationData>;
      const loc = copy[currentLocation];
      if (!loc || !loc.siteLayout) return prev;
      const cells = loc.siteLayout.cells.map(row => row.slice());
      const cell = cells[y][x] as any;
      // Don't paint over rooms unless erasing
      if (cell.type === 'roomRef' && tool !== 'eraser') {
        const now = Date.now();
        if (now - lastWarnRef.current > 600) {
          lastWarnRef.current = now;
          triggerNotification('Cannot paint over a floor/room. Use Eraser or Move the room.', 'warning');
        }
        return prev;
      }
      const newType = tool === 'eraser' ? 'empty' : tool;
      if (cell.type === newType) return prev;
      cells[y][x] = { type: newType } as any;
      copy[currentLocation] = { ...loc, siteLayout: { ...loc.siteLayout, cells } as any };
      return copy;
    });
  }, [currentLocation, setLocations]);

  const moveRoomTo = useCallback((roomId: string, nx: number, ny: number) => {
    setLocations(prev => {
      const copy = { ...prev } as Record<string, LocationData>;
      const loc = copy[currentLocation];
      if (!loc || !loc.rooms || !loc.siteLayout) return prev;
      const rooms = { ...(loc.rooms as any) };
      const room = rooms[roomId];
      if (!room?.bounds) return prev;
      const w = room.bounds.width;
      const h = room.bounds.height;
      // Clamp within grid
      const clampedX = Math.max(0, Math.min(TILE_W - w, nx));
      const clampedY = Math.max(0, Math.min(TILE_H - h, ny));
      // Clone cells
      const cells = loc.siteLayout.cells.map(row => row.slice());
      // Clear previous footprint of this room
      for (let y = 0; y < TILE_H; y++) {
        for (let x = 0; x < TILE_W; x++) {
          const c = cells[y][x] as any;
          if (c.type === 'roomRef' && c.roomId === roomId) cells[y][x] = { type: 'empty' } as any;
        }
      }
      // Paint new footprint
      for (let y = clampedY; y < clampedY + h; y++) {
        for (let x = clampedX; x < clampedX + w; x++) {
          cells[y][x] = { type: 'roomRef', roomId } as any;
        }
      }
      rooms[roomId] = { ...room, bounds: { ...room.bounds, x: clampedX, y: clampedY } };
      copy[currentLocation] = { ...loc, rooms, siteLayout: { ...loc.siteLayout, cells } as any };
      return copy;
    });
  }, [currentLocation, setLocations]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Size the canvas (viewport), independent from world scale
    const width = Math.min(1440, TILE_W * 8);
    const height = Math.min(900, TILE_H * 8);
    canvas.width = width;
    canvas.height = height;

    const getMousePos = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      return { x, y };
    };

    const screenToWorld = (sx: number, sy: number) => {
      const scale = scaleRef.current;
      const off = offsetRef.current;
      return { x: (sx - off.x) / scale, y: (sy - off.y) / scale };
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { x: sx, y: sy } = getMousePos(e as unknown as MouseEvent);
      const worldBefore = screenToWorld(sx, sy);
      const factor = Math.exp(-e.deltaY * 0.0015);
      const oldScale = scaleRef.current;
      let newScale = oldScale * factor;
      newScale = Math.max(0.5, Math.min(20, newScale));
      scaleRef.current = newScale;
      // keep cursor-centered zoom
      offsetRef.current.x = sx - worldBefore.x * newScale;
      offsetRef.current.y = sy - worldBefore.y * newScale;
      draw();
    };

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      // Left click: use tool; Right/Middle: pan
      if (e.button === 0) {
        const { x: sx, y: sy } = getMousePos(e as unknown as MouseEvent);
        const world = screenToWorld(sx, sy);
        const tile = {
          x: Math.max(0, Math.min(TILE_W - 1, Math.floor(world.x))),
          y: Math.max(0, Math.min(TILE_H - 1, Math.floor(world.y)))
        };
        const snap = { x: Math.floor(tile.x / SNAP) * SNAP, y: Math.floor(tile.y / SNAP) * SNAP };

        // Room hit detection
        const roomsMap = (locations[currentLocation]?.rooms as Record<string, any>) || {};
        const hitRoom = Object.values(roomsMap).find((room: any) => {
          const b = room?.bounds;
          if (!b) return false;
          return tile.x >= b.x && tile.x < b.x + b.width && tile.y >= b.y && tile.y < b.y + b.height;
        });

        // Move tool: begin dragging if over a room
        if (selectedTool === 'move' && hitRoom) {
          const b = (hitRoom as any).bounds;
          draggingRoomRef.current = { roomId: (hitRoom as any).id, offset: { dx: tile.x - b.x, dy: tile.y - b.y } };
          if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing';
          return;
        }

        // Open floor/room only when using the room tool
        if (selectedTool === 'room' && hitRoom) {
          const floorId: string | undefined = (hitRoom as any).floorId;
          if (floorId && onOpenFloor) {
            onOpenFloor(floorId);
          } else if (onOpenRoom) {
            const template: 'full' | 'half' = (hitRoom as any).type === 'cage' ? 'full' : 'half';
            onOpenRoom((hitRoom as any).id, template);
          }
          return;
        }

        // Painting tools: start painting immediately
        if (selectedTool === 'wall' || selectedTool === 'door' || selectedTool === 'walkway' || selectedTool === 'eraser' || selectedTool === 'tourniquetDoor' || selectedTool === 'extinguisher' || selectedTool === 'bin' || selectedTool === 'cabinet') {
          paintingRef.current = { active: true, type: selectedTool } as any;
          paintAt(snap.x, snap.y, selectedTool as any);
          draw();
          return;
        }

        if (selectedTool === 'room') {
          placingRef.current = { start: snap, current: snap };
          draw();
          return;
        }
        // Other tools not yet implemented: no-op for now
      } else {
        // Pan for non-left buttons
        isPanningRef.current = true;
        lastPosRef.current = { x: e.clientX, y: e.clientY };
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      const { x: sx, y: sy } = getMousePos(e as unknown as MouseEvent);
      const world = screenToWorld(sx, sy);
      const tile = {
        x: Math.max(0, Math.min(TILE_W - 1, Math.floor(world.x))),
        y: Math.max(0, Math.min(TILE_H - 1, Math.floor(world.y)))
      };
      const snap = { x: Math.floor(tile.x / SNAP) * SNAP, y: Math.floor(tile.y / SNAP) * SNAP };
      const siteCells = (locations[currentLocation]?.siteLayout?.cells as any) || null;
      const cellType = siteCells ? (siteCells[tile.y]?.[tile.x]?.type || 'empty') : undefined;

      // Cursor affordance: pointer when hovering a room
      const roomsMap = (locations[currentLocation]?.rooms as Record<string, any>) || {};
      const hitRoom = Object.values(roomsMap).find((room: any) => {
        const b = room?.bounds;
        if (!b) return false;
        return tile.x >= b.x && tile.x < b.x + b.width && tile.y >= b.y && tile.y < b.y + b.height;
      });
      if (canvasRef.current) {
        if (draggingRoomRef.current) canvasRef.current.style.cursor = 'grabbing';
        else if (selectedTool === 'move' && hitRoom) canvasRef.current.style.cursor = 'grab';
        else canvasRef.current.style.cursor = hitRoom && selectedTool === 'room' ? 'pointer' : 'crosshair';
      }

      setHoverInfo({ tile, snap, cellType });
      if (isPanningRef.current && lastPosRef.current) {
        const dx = e.clientX - lastPosRef.current.x;
        const dy = e.clientY - lastPosRef.current.y;
        offsetRef.current.x += dx;
        offsetRef.current.y += dy;
        lastPosRef.current = { x: e.clientX, y: e.clientY };
        draw();
        return;
      }
      // Dragging a room with Move tool
      if (draggingRoomRef.current && selectedTool === 'move') {
        const { roomId, offset } = draggingRoomRef.current;
        moveRoomTo(roomId, tile.x - offset.dx, tile.y - offset.dy);
        draw();
        return;
      }
      if (placingRef.current && selectedTool === 'room') {
        placingRef.current.current = snap;
        draw();
        return;
      }
      if (paintingRef.current?.active && (selectedTool === 'wall' || selectedTool === 'door' || selectedTool === 'walkway' || selectedTool === 'eraser' || selectedTool === 'tourniquetDoor' || selectedTool === 'extinguisher' || selectedTool === 'bin' || selectedTool === 'cabinet')) {
        paintAt(snap.x, snap.y, paintingRef.current.type);
        draw();
        return;
      }
      draw();
    };

    const onMouseUp = () => {
      // Commit room placement if in progress
      if (placingRef.current && selectedTool === 'room') {
        const cur = placingRef.current.current;
        const px = Math.max(0, Math.min(TILE_W - 18, cur.x));
        const py = Math.max(0, Math.min(TILE_H - 8, cur.y));
        const newId = `room-${Date.now()}`;
        const newFloorId = `floor-${Date.now()}`;
        setLocations(prev => {
          const copy = { ...prev } as Record<string, LocationData>;
          const loc = copy[currentLocation];
          if (!loc) return prev;
          const nextRooms = { ...(loc.rooms || {}) } as Record<string, any>;
          const floorNumber = Object.keys(loc.floors || {}).length + 1;
          const name = `Floor ${floorNumber}`;
          const newRoom = {
            id: newId,
            name,
            type: 'cage',
            color: '#2563eb',
            bounds: { x: px, y: py, width: 18, height: 8 },
            floorId: newFloorId,
          };
          // Ensure siteLayout exists and clone cells for immutable update
          let siteLayout = loc.siteLayout;
          if (!siteLayout) {
            const cells = Array.from({ length: TILE_H }, () =>
              Array.from({ length: TILE_W }, () => ({ type: 'empty' as const }))
            );
            siteLayout = { width: 128 as const, height: 96 as const, cells } as any;
          } else {
            siteLayout = { ...siteLayout, cells: siteLayout.cells.map(row => row.slice()) } as any;
          }
          for (let y = py; y < py + 8; y++) {
            for (let x = px; x < px + 18; x++) {
              (siteLayout as any).cells[y][x] = { type: 'roomRef', roomId: newId } as any;
            }
          }
          nextRooms[newId] = newRoom;

          // Auto-create a floor for this cage room with an 18×8 empty grid
          const emptyGrid: GridCell[][] = Array.from({ length: 8 }, () =>
            Array.from({ length: 18 }, () => ({ type: 'empty' as CellType }))
          );
          const newFloor = {
            id: newFloorId,
            name,
            level: 0,
            racks: [],
            coolingCapacity: 0,
            usedPower: 0,
            usedCooling: 0,
            floorQualityId: 'basic',
            designGrid: emptyGrid,
          };
          const nextFloors = { ...(loc.floors || {}), [newFloorId]: newFloor };
          const nextRoomAssignments = { ...(loc.roomAssignments || {}), [newId]: newFloorId };

          copy[currentLocation] = { ...loc, siteLayout, rooms: nextRooms, floors: nextFloors, roomAssignments: nextRoomAssignments };
          return copy;
        });
        triggerNotification('New server room and floor created. Open it to design the layout.', 'success');
        placingRef.current = null;
        draw();
      }
      // Stop painting
      paintingRef.current = null;
      // Stop dragging
      if (draggingRoomRef.current) {
        draggingRoomRef.current = null;
        if (canvasRef.current) canvasRef.current.style.cursor = 'crosshair';
      }
      isPanningRef.current = false;
      lastPosRef.current = null;
    };

    const onContextMenu = (e: MouseEvent) => e.preventDefault();

    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('contextmenu', onContextMenu);

    // Set default zoom once and center the world inside the canvas
    if (!initializedRef.current) {
      const targetScale = 6.95;
      scaleRef.current = targetScale;
      const worldW = TILE_W * targetScale;
      const worldH = TILE_H * targetScale;
      offsetRef.current.x = Math.round((canvas.width - worldW) / 2);
      offsetRef.current.y = Math.round((canvas.height - worldH) / 2);
      initializedRef.current = true;
    }
    draw();

    return () => {
      canvas.removeEventListener('wheel', onWheel as EventListener);
      canvas.removeEventListener('mousedown', onMouseDown as EventListener);
      window.removeEventListener('mousemove', onMouseMove as EventListener);
      window.removeEventListener('mouseup', onMouseUp as EventListener);
      canvas.removeEventListener('contextmenu', onContextMenu as EventListener);
    };
  }, [draw]);

  useEffect(() => {
    draw();
  }, [locations, currentLocation, draw]);

  useEffect(() => {
    // Normalize the roomRef layer from authoritative room bounds
    setLocations(prev => {
      const copy = { ...prev } as Record<string, LocationData>;
      const loc = copy[currentLocation];
      if (!loc || !loc.siteLayout) return prev;

      const width = TILE_W;
      const height = TILE_H;

      const originalCells = loc.siteLayout.cells;
      const nextCells = originalCells.map(row => row.slice());

      // Clear any existing roomRef cells
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const c = nextCells[y][x] as any;
          if (c && c.type === 'roomRef') {
            nextCells[y][x] = { type: 'empty' } as any;
          }
        }
      }

      // Paint room footprints based on authoritative room bounds
      const roomsMap = (loc.rooms as Record<string, any>) || {};
      Object.values(roomsMap).forEach((room: any) => {
        const b = room?.bounds;
        if (!b) return;
        for (let y = b.y; y < b.y + b.height; y++) {
          if (y < 0 || y >= height) continue;
          for (let x = b.x; x < b.x + b.width; x++) {
            if (x < 0 || x >= width) continue;
            nextCells[y][x] = { type: 'roomRef', roomId: room.id } as any;
          }
        }
      });

      // Detect differences to avoid unnecessary updates
      let changed = false;
      outer: for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const a = originalCells[y][x] as any;
          const b = nextCells[y][x] as any;
          if (a.type !== b.type || (a.type === 'roomRef' && a.roomId !== b.roomId)) {
            changed = true;
            break outer;
          }
        }
      }

      if (!changed) return prev;

      copy[currentLocation] = { ...loc, siteLayout: { ...loc.siteLayout, cells: nextCells } as any };
      return copy;
    });
  }, [currentLocation, locations, setLocations]);

  useEffect(() => {
    setLocations(prev => {
      const copy = { ...prev } as Record<string, LocationData>;
      const loc = copy[currentLocation];
      if (!loc) return prev;
      let changed = false;
      if (!loc.siteLayout) {
        const cells = Array.from({ length: TILE_H }, () =>
          Array.from({ length: TILE_W }, () => ({ type: 'empty' as const }))
        );
        copy[currentLocation] = { ...loc, siteLayout: { width: 128 as const, height: 96 as const, cells }, rooms: loc.rooms || {} };
        return copy;
      }
      if (!loc.rooms) {
        copy[currentLocation] = { ...loc, rooms: {} };
        changed = true;
      }
      return changed ? copy : prev;
    });
  }, [currentLocation, setLocations]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">Site Layout Editor — {locations[currentLocation]?.name ?? currentLocation}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Main content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Tools sidebar */}
          <div className="w-64 bg-gray-800 p-4 flex flex-col">
            <h3 className="font-medium text-white mb-4">Tools</h3>
            <div className="grid grid-cols-2 gap-2">
              {TOOL_ORDER.map(t => (
                <button
                  key={t}
                  className={`px-2 py-1 rounded text-sm ${selectedTool === t ? 'bg-purple-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-200'}`}
                  onClick={() => setSelectedTool(t)}
                >
                  {TOOL_LABELS[t]}
                </button>
              ))}
            </div>
            <div className="mt-4 text-xs text-gray-400">
              Snap: 1×1 tiles. Left-click: use tool (Room=place, Move=drag, Others=paint). Right/Middle: pan. Wheel: zoom.
            </div>
            <div className="mt-3 text-xs text-gray-400">
              <div className="text-gray-300 font-medium mb-1">Legend</div>
              <div className="flex items-center gap-2 mb-1">
                <span style={{ width: 12, height: 12, backgroundColor: '#10b981', display: 'inline-block' }} />
                <span>Assigned floor indicator</span>
              </div>
              <div>Thicker border = assigned floor</div>
            </div>

            {/* Facility Safety — live bonuses from placed equipment */}
            {(() => {
              const s = computeFacilitySafety(locations[currentLocation]);
              const pct = (v: number) => `${Math.round(v * 100)}%`;
              const Row = ({ icon, label, count, effect, active }: { icon: string; label: string; count: number; effect: string; active: boolean }) => (
                <div className="flex items-start gap-2 mb-1.5">
                  <span className="text-base leading-none">{icon}</span>
                  <div className="flex-1">
                    <div className="flex justify-between text-gray-200">
                      <span>{label}</span>
                      <span className="tabular-nums">×{count}</span>
                    </div>
                    <div className={active ? 'text-emerald-400' : 'text-gray-500'}>{effect}</div>
                  </div>
                </div>
              );
              return (
                <div className="mt-4 text-xs">
                  <div className="text-gray-300 font-medium mb-2">Facility Safety</div>
                  <Row icon="🧯" label="Extinguishers" count={s.extinguishers} active={s.coolingFailureReduction > 0}
                    effect={`−${pct(s.coolingFailureReduction)} cooling-failure risk`} />
                  <Row icon="🚪" label="Access doors" count={s.accessControl} active={s.hardwareRecallReduction > 0}
                    effect={`−${pct(s.hardwareRecallReduction)} hardware-recall risk`} />
                  <Row icon="🗄️" label="Spare cabinets" count={s.cabinets} active={s.repairTimeReduction > 0}
                    effect={`−${pct(s.repairTimeReduction)} repair time`} />
                  <Row icon="🗑️" label="Bins" count={s.bins} active={s.failureReduction > 0}
                    effect={`−${pct(s.failureReduction)} failure rate`} />
                  <div className="mt-1 text-gray-500">Place equipment to lower outage risk and speed repairs at this site.</div>
                </div>
              );
            })()}

            <div className="flex-1" />
          </div>

          {/* Grid area */}
          <div className="flex-1 bg-gray-900 p-6 overflow-auto">
            <div className="inline-block bg-gray-800 p-2 rounded-lg">
              <canvas ref={canvasRef} className="block" style={{ imageRendering: 'pixelated', cursor: 'crosshair' }} />
            </div>
            <div className="mt-4 text-sm text-gray-300">
              {hoverInfo ? (
                <span>
                  Hover tile: ({hoverInfo.tile.x},{hoverInfo.tile.y}) • Snap: ({hoverInfo.snap.x},{hoverInfo.snap.y})
                  {typeof hoverInfo.cellType !== 'undefined' && (
                    <> • Cell: <span className="font-medium">{hoverInfo.cellType}</span>{hoverInfo.cellType === 'roomRef' ? ' (blocked)' : ''}</>
                  )}
                </span>
              ) : (
                <span>Hover over the grid to see coordinates.</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteLayoutEditor;
